import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { createRoomMember } from '../services/frontendDemoAPI';
import { socket } from '../services/frontendDemoAPI';
import { useNavigate } from 'react-router-dom';

const RoomContext = createContext();
const apiUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';

export function RoomProvider({ children }) {
  const [room, setRoom] = useState(null); // { id, name, members, createdBy }
  // Initialize username from localStorage if available (for current room)
  const [username, setUsername] = useState(() => {
    // Try to find any room id in the URL
    const match = window.location.pathname.match(/room\/(\w+)/);
    if (match) {
      const roomId = match[1];
      try {
        const stored = JSON.parse(localStorage.getItem(`room_${roomId}_user`));
        return stored?.username || '';
      } catch {
        return '';
      }
    }
    return '';
  });
  const [members, setMembers] = useState([]);
  const unsubscribeRef = useRef(null);
  const navigate = useNavigate();

  const fetchMembers = useCallback(async (roomId) => {
    try {
      const res = await fetch(`${apiUrl}/api/room/${roomId}/members`);
      if (!res.ok) throw new Error('Failed to fetch members');
      const data = await res.json();
      setMembers(data);
    } catch (e) {
      setMembers([]);
    }
  }, []);

  const joinRoom = useCallback(async (roomData, user) => {
    setRoom(roomData);
    setUsername(user);
    try {
      await createRoomMember(roomData.id, user);
      await fetchMembers(roomData.id);
      // Subscribe to real-time member updates
      const event = `room_members_update_${roomData.id}`;
      socket.emit('join_room', roomData.id);
      socket.on(event, setMembers);
      // Store unsubscribe for later
      unsubscribeRef.current = () => {
        socket.emit('leave_room', roomData.id);
        socket.off(event, setMembers);
      };
    } catch (e) {
      // Optionally handle error
    }
  }, [fetchMembers]);

  const leaveRoom = useCallback(async () => {
    const currentRoom = room;
    const currentUsername = username;
    if (currentRoom && currentUsername) {
      try {
        await fetch(`${apiUrl}/api/room/${currentRoom.id}/leave`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: currentUsername })
        });
      } catch (e) {
        // Optionally handle error
      }
    }
    // Unsubscribe from socket events
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    // Clear localStorage for this room
    if (currentRoom) {
      navigate(`/room/${currentRoom.id}`); // Redirect to join page after leaving
      localStorage.removeItem(`room_${currentRoom.id}_user`);
    }

    // setRoom(null);
    // setUsername('');
    // setMembers([]);
  }, [room, username, navigate]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) unsubscribeRef.current();
    };
  }, []);

  return (
    <RoomContext.Provider value={{ room, username, members, joinRoom, leaveRoom, setUsername }}>
      {children}
    </RoomContext.Provider>
  );
}

export function useRoom() {
  return useContext(RoomContext);
}
